import { Card, CardContent } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { getFileUrl } from "@shared/utils/fileUrl";
import {
  Warehouse,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Unlock,
  Lock,
  Puzzle,
  Ban,
  RefreshCw,
  Slash,
  Edit,
  Trash2,
  RotateCcw,
  Store,
} from "lucide-react";
import { Separator } from "@shared/components/ui/separator";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@shared/components/ui/button";
import { useTranslation } from "react-i18next";
import { useDeleteProduct } from "@features/products/hooks/useProducts";
import { toast } from "sonner";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@shared/components/ui/avatar";
import { cn } from "@/lib/utils";
import {
  PRODUCT_STATUS_OPTIONS,
  PRODUCT_TOAST_KEYS,
} from "@features/products/constants";

function IconWithTooltip({ Icon, color, title }) {
  return (
    <div className="flex items-center gap-1">
      <Icon className={`w-5 h-5 ${color}`} />
      <span className="text-xs">{title}</span>
    </div>
  );
}

// Icon mapping for product status
const ICON_MAP = {
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
};

export default function ProductCard({ product, className }) {
  const {
    allowExchange,
    allowFitting,
    allowOpening,
    isActive,
    status = isActive ? "active" : "inactive", // Fallback to isActive if status not provided
    price,
    name,
    stock,
    quantityOut,
    quantityIn,
    image,
    _id,
  } = product;
  const { t } = useTranslation();
  const { deleteProduct, isLoading } = useDeleteProduct();
  const navigate = useNavigate();

  const statusOption = PRODUCT_STATUS_OPTIONS.find(
    (opt) => opt.value === status
  );
  const StatusIcon = statusOption?.icon ? ICON_MAP[statusOption.icon] : null;

  const handleDelete = (isActive) => {
    const toastId = toast.loading(t("toast:" + PRODUCT_TOAST_KEYS.UPDATING));
    deleteProduct(
      {
        id: _id,
        values: { isActive: !isActive },
      },
      {
        onSuccess: () => {
          toast.success(t("toast:" + PRODUCT_TOAST_KEYS.UPDATED_SUCCESS), {
            id: toastId,
          });
        },
        onError: (error) => {
          toast.error(`${error.response.data.message}`, {
            id: toastId,
          });
        },
      }
    );
  };

  return (
    <Card className={cn("w-full shadow-md", className)}>
      <div>
        <div className="flex flex-col md:flex-row flex-wrap gap-2 items-center justify-between p-3">
          <div
            className="px-2 cursor-pointer"
            onClick={() => {
              navigate(`/products/${_id}/view`);
            }}
          >
            <Avatar className="size-24 rounded-full">
              <AvatarImage src={getFileUrl(`products/${image}`)} />
              <AvatarFallback className="uppercase rounded-full">
                {name.substring(0, 2)}
              </AvatarFallback>
            </Avatar>
          </div>
          <div className="md:flex-1">
            <CardContent>
              <div>
                <div className="flex flex-col md:flex-row justify-between items-center">
                  <h3
                    className="text-lg font-semibold capitalize truncate cursor-pointer"
                    onClick={() => {
                      navigate(`/products/${_id}/view`);
                    }}
                  >
                    {name}
                  </h3>
                  <div className="flex items-center gap-2">
                    <Badge variant={statusOption?.variant || "secondary"}>
                      {StatusIcon && (
                        <StatusIcon className="w-4 h-4 ltr:mr-1 rtl:ml-1" />
                      )}
                      {t(`${statusOption?.label}`)}
                    </Badge>
                    <Link to={`/products/${_id}/edit`}>
                      <Edit className="w-5 h-5 text-gray-500 cursor-pointer" />
                    </Link>

                    {status === "active" || isActive ? (
                      <Button
                        size="icon"
                        variant="ghost"
                        disabled={isLoading}
                        onClick={() => {
                          handleDelete(isActive);
                        }}
                      >
                        <Trash2 className="w-5 h-5 text-red-500 cursor-pointer" />
                      </Button>
                    ) : (
                      <Button
                        size="icon"
                        variant="ghost"
                        disabled={isLoading}
                        onClick={() => {
                          handleDelete(isActive);
                        }}
                      >
                        <RotateCcw className="w-5 h-5 text-green-500 cursor-pointer" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <div
                className="space-y-2 cursor-pointer"
                onClick={() => {
                  navigate(`/products/${_id}/view`);
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Warehouse className="w-4 h-4 mx-1" />
                    <span className="font-semibold">
                      {stock}{" "}
                      <span className="text-gray-400">
                        ( - {quantityOut} | + {quantityIn})
                      </span>{" "}
                      {t("products.products")}
                    </span>
                  </div>

                  <span className="font-semibold">
                    {price.toFixed(2)} {t("main.currency")}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Store className="w-4 h-4 mx-1" />
                    <span className="font-semibold">
                      {product.user.brandName}
                    </span>
                  </div>
                </div>
                <Separator />
                <div className="flex justify-between pt-2">
                  <IconWithTooltip
                    Icon={allowOpening ? Unlock : Lock}
                    color={allowOpening ? "text-green-500" : "text-red-500"}
                    title={
                      allowOpening
                        ? t("products.opening")
                        : t("products.noOpening")
                    }
                  />
                  <IconWithTooltip
                    Icon={allowFitting ? Puzzle : Ban}
                    color={allowFitting ? "text-green-500" : "text-red-500"}
                    title={
                      allowFitting
                        ? t("products.fitting")
                        : t("products.noFitting")
                    }
                  />
                  <IconWithTooltip
                    Icon={allowExchange ? RefreshCw : Slash}
                    color={allowExchange ? "text-green-500" : "text-red-500"}
                    title={
                      allowExchange
                        ? t("products.exchange")
                        : t("products.noExchange")
                    }
                  />
                </div>
              </div>
            </CardContent>
          </div>
        </div>
      </div>
    </Card>
  );
}
